import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Eye, Trash2, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || '/api';

export default function Duplicates() {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['duplicateGroups'],
    queryFn: async () => {
      const response = await axios.get(`${API_URL}/reconciliation/duplicates`);
      return response.data;
    },
  });

  const resolveMutation = useMutation({
    mutationFn: async ({ groupId, keepDocId, deleteOthers }: { groupId: number; keepDocId: string; deleteOthers: boolean }) => {
      const response = await axios.post(`${API_URL}/reconciliation/duplicates/${groupId}/resolve`, {
        keepDocumentId: keepDocId,
        deleteOthers,
      });
      return response.data;
    },
    onSuccess: () => {
      toast.success('Duplicate group resolved');
      queryClient.invalidateQueries({ queryKey: ['duplicateGroups'] });
      queryClient.invalidateQueries({ queryKey: ['documents'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to resolve duplicates');
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  const groups = data?.groups || [];
  const unresolvedGroups = groups.filter((g: any) => !g.resolved);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Duplicate Documents</h1>
        <p className="text-gray-600">
          Review and manage duplicate documents detected in your system
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-10 w-10 text-yellow-600" />
            <div>
              <p className="text-sm text-gray-600">Unresolved Groups</p>
              <p className="text-2xl font-bold text-gray-900">{unresolvedGroups.length}</p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center gap-3">
            <CheckCircle className="h-10 w-10 text-green-600" />
            <div>
              <p className="text-sm text-gray-600">Resolved Groups</p>
              <p className="text-2xl font-bold text-gray-900">
                {groups.filter((g: any) => g.resolved).length}
              </p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-10 w-10 text-gray-400" />
            <div>
              <p className="text-sm text-gray-600">Total Groups</p>
              <p className="text-2xl font-bold text-gray-900">{groups.length}</p>
            </div>
          </div>
        </div>
      </div>

      {unresolvedGroups.length === 0 ? (
        <div className="card text-center py-12">
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            No Duplicates Found!
          </h2>
          <p className="text-gray-600">
            All documents are unique or duplicates have been resolved.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {unresolvedGroups.map((group: any) => (
            <DuplicateGroupCard
              key={group.id}
              group={group}
              onResolve={(keepDocId, deleteOthers) =>
                resolveMutation.mutate({ groupId: group.id, keepDocId, deleteOthers })
              }
            />
          ))}
        </div>
      )}

      {/* Resolved groups */}
      {groups.filter((g: any) => g.resolved).length > 0 && (
        <div className="mt-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Resolved Duplicates
          </h2>
          <div className="space-y-2">
            {groups
              .filter((g: any) => g.resolved)
              .map((group: any) => (
                <div key={group.id} className="card">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          Group #{group.id}
                        </p>
                        <p className="text-xs text-gray-500">{group.resolution_note}</p>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500">
                      {group.document_count} documents
                    </p>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}

interface DuplicateGroupCardProps {
  group: any;
  onResolve: (keepDocId: string, deleteOthers: boolean) => void;
}

function DuplicateGroupCard({ group, onResolve }: DuplicateGroupCardProps) {
  const { data: documents } = useQuery({
    queryKey: ['duplicateDocuments', group.id],
    queryFn: async () => {
      const response = await axios.get(`${import.meta.env.VITE_API_URL || '/api'}/reconciliation/duplicates/${group.id}/documents`);
      return response.data.documents;
    },
  });

  if (!documents) {
    return (
      <div className="card animate-pulse">
        <div className="h-20 bg-gray-200 rounded" />
      </div>
    );
  }

  const primary = documents.find((d: any) => d.is_primary);

  return (
    <div className="card border-yellow-200 bg-yellow-50">
      <div className="flex items-start gap-4">
        <AlertTriangle className="h-8 w-8 text-yellow-600 flex-shrink-0 mt-1" />
        
        <div className="flex-1">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Duplicate Group #{group.id}
              </h3>
              <p className="text-sm text-gray-600">
                {documents.length} identical documents detected
              </p>
            </div>
          </div>

          <div className="space-y-2">
            {documents.map((doc: any) => (
              <div
                key={doc.document_id}
                className={`flex items-center justify-between p-3 rounded-lg ${
                  doc.is_primary ? 'bg-white border-2 border-yellow-400' : 'bg-white'
                }`}
              >
                <div className="flex items-center gap-3">
                  {doc.is_primary && (
                    <span className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs font-medium rounded">
                      ORIGINAL
                    </span>
                  )}
                  <div>
                    <p className="text-sm font-medium text-gray-900">{doc.filename}</p>
                    <p className="text-xs text-gray-500">
                      Uploaded {new Date(doc.upload_date).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                
                <Link
                  to={`/documents/${doc.document_id}`}
                  className="btn-ghost text-sm"
                >
                  <Eye className="h-4 w-4" />
                  View
                </Link>
              </div>
            ))}
          </div>

          <div className="mt-4 pt-4 border-t border-yellow-200 flex gap-2">
            <button
              onClick={() => onResolve(primary?.document_id || documents[0].document_id, false)}
              className="btn-secondary text-sm"
            >
              Mark as Reviewed (Keep All)
            </button>
            <button
              onClick={() => {
                if (window.confirm(`Delete ${documents.length - 1} duplicate(s) and keep only the original?`)) {
                  onResolve(primary?.document_id || documents[0].document_id, true);
                }
              }}
              className="btn-danger text-sm"
            >
              <Trash2 className="h-4 w-4" />
              Delete Duplicates
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


